import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SYNCED_STORES } from "@/stores/synced-stores";
import { isWorkshopBlobSyncEnabled } from "@/lib/workshop-blob-sync";
import { onCrossTabSync } from "@/lib/sync-broadcast";
import { useAuth } from "@/hooks/use-auth";

/**
 * Phase 6 — polling layer that periodically reconciles the AssetLocker
 * and Captures stores against whichever blob backend is active: the
 * production BFF / Azure backend or the legacy GitHub fallback (the choice
 * lives in `workshop-blob-sync.ts`; this layer is backend-agnostic).
 *
 * Mounted ONCE (at the top of `AppLayout` — see `src/components/shell/
 * AppLayout.tsx`) so it runs for the lifetime of an authenticated
 * session, not per-page. The TanStack Query under the hood:
 *
 *   - calls both stores' `sync()` in parallel via `Promise.all`,
 *   - reruns every 15 s (`refetchInterval`),
 *   - reruns when the tab regains focus (`refetchOnWindowFocus`),
 *   - dedupes back-to-back invalidations via a 10 s `staleTime`,
 *   - stays inert when sync is disabled (`enabled: false` short-circuits
 *     the `queryFn` so the no-PAT / logged-out path pays no cost).
 *
 * The hook ALSO subscribes to {@link onCrossTabSync} so a sibling tab's
 * backend write triggers an immediate `invalidateQueries` here — that's
 * how a second tab picks up a captured snapshot without waiting for
 * the next 15 s tick. See `docs/adr/0010-github-persistence.md` →
 * "Migration path" → step 3 for the rationale.
 *
 * No return value — consumers just call it for the side effect. The
 * sync status surfaces through each store's `syncState` / `syncError`
 * fields, which the header pill (`SyncStatusPill`) reads directly.
 */
export function useGithubSync(): void {
  // Runs for whichever blob backend is active (BFF or the legacy GitHub
  // mirror); inert when neither is configured (IDB-only) OR the user
  // hasn't signed in yet. Gating on `user` is load-bearing on live:
  // AppLayout mounts this hook on the login page too, and an unauthenticated
  // poll used to stamp every store with `syncError: "Not signed in"`, which
  // then stuck on the Asset Locker footer after sign-in until the next tick.
  const { user } = useAuth();
  const enabled = isWorkshopBlobSyncEnabled() && !!user;
  const queryClient = useQueryClient();

  // The queryKey is shared with the cross-tab listener below — both
  // funnel through `invalidateQueries(["github-sync"])` so the
  // reconciliation flow is the same whether the trigger came from the
  // 15 s tick, a tab focus, a sibling tab, or a manual click on the
  // header pill.
  useQuery({
    queryKey: ["github-sync"],
    queryFn: async () => {
      // Every registered store's `sync()` is idempotent + safe to run in
      // parallel (they write to disjoint workshop subdirectories and never
      // share an Octokit call). Add a store to SYNCED_STORES
      // (`src/stores/synced-stores.ts`) to include it here — no edit needed
      // in this file (P0.7 anti-collision primitive).
      await Promise.all(SYNCED_STORES.map((s) => s.getState().sync()));
      // The returned value is informational — TanStack uses it as the
      // cache value but the consumers read sync state off the Zustand
      // stores, not off the query result.
      return Date.now();
    },
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    enabled,
    // 10 s dedupe — guards against the focus event + the cross-tab
    // ping landing back-to-back from the same upstream write (the
    // common case when a user alt-tabs back to a tab they just edited
    // in another window).
    staleTime: 10_000,
  });

  useEffect(() => {
    // Short-circuit the listener registration when sync is disabled —
    // the cross-tab ping wouldn't have anything to do anyway, and we
    // don't want to construct a `BroadcastChannel` just to throw the
    // first message away.
    if (!enabled) return;
    const unsubscribe = onCrossTabSync(() => {
      void queryClient.invalidateQueries({ queryKey: ["github-sync"] });
    });
    return unsubscribe;
  }, [enabled, queryClient]);
}
